import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser, useViewerRoles } from '@treams/treams-hooks';
import { DeleteIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, IconButton, Paragraph, Select, Separator, Spinner } from '@treams/treams-ui';

import { IQueueJoinInput } from './LearningEventInviteAttendee';

export default function LearningEventAttendee(props: {
	user: ComponentProps<typeof Avatar>['user'] & Pick<graphql.User, 'id'>;
	type: graphql.LearningEventAttendeeType;
	event?: graphql.LearningEvent;
	regular?: boolean;
	readonly?: boolean;
	queueJoin?: (input: IQueueJoinInput) => void;
	unqueueJoin?: (id: string) => void;
}) {
	const { t } = useTranslation();
	const authenticatedUser = useAuthenticatedUser();

	const roles = useViewerRoles();

	const options = [
		{
			title: t('learning.event.modal.host.type.hostOrganizer'),
			value: graphql.LearningEventAttendeeType.HostOrganizer,
		},
		{
			title: t('learning.event.modal.host.type.host'),
			value: graphql.LearningEventAttendeeType.Host,
		},
		{
			title: t('learning.event.modal.host.type.organizer'),
			value: graphql.LearningEventAttendeeType.Organizer,
		},
	];

	if (props.regular) {
		options.unshift({
			title: t('learning.event.modal.host.type.attendee'),
			value: graphql.LearningEventAttendeeType.Attendee,
		});
	}

	const value = options.find((option) => option.value === props.type);

	const [join, { loading: joinLoading }] = graphql.useJoinLearningEventMutation();
	const [leave, { loading: leaveLoading }] = graphql.useLeaveLearningEventMutation();

	const loading = joinLoading || leaveLoading;

	// Hide the leave button if:
	// - event doesn't exist yet and authenticated user is the attendee (creator can't be removed)
	// - event does exist and attendee is the creator (creator can't be removed)
	// - user doesn't have sufficient roles

	const hasWorkspaceRole = roles.isWorkspaceSuperuser || roles.isLearningOfficer;

	const hideLeaveButton =
		(!props.event && props.user.id === authenticatedUser.id) ||
		(props.event && props.user.id === props.event.createdBy.id) ||
		(!hasWorkspaceRole && props.event?.createdBy.id !== authenticatedUser.id);

	return (
		<>
			<Flex flexDirection="row" alignItems="center" my={2}>
				<Box mr={3}>
					<Avatar user={props.user} size={24} />
				</Box>
				<Box flexGrow={1}>
					<Paragraph>
						{props.user.firstName} {props.user.lastName}
					</Paragraph>
				</Box>

				{props.readonly ? null : loading ? (
					<Spinner />
				) : (
					<>
						<Box alignSelf="center" ml={2} flex="240px" flexShrink={0} flexGrow={0} width={240}>
							<Select
								selectedValue={value?.value}
								options={options}
								onChange={(value) => {
									if (!props.event) {
										props.queueJoin!({
											...props.user,
											attendeeType: value as graphql.LearningEventAttendeeType,
										});

										return;
									}

									join({
										variables: {
											input: {
												learningEventID: props.event!.id,
												userID: props.user.id!,
												attendeeType: value as graphql.LearningEventAttendeeType,
											},
										},
									});
								}}
							/>
						</Box>
						<Box alignSelf="center" ml={4} width={32}>
							{!hideLeaveButton && (
								<IconButton
									icon={<DeleteIcon />}
									type="destructive"
									onClick={() => {
										if (!props.event) {
											props.unqueueJoin!(props.user.id!);

											return;
										}

										leave({
											variables: {
												input: {
													learningEventID: props.event!.id,
													userID: props.user.id!,
												},
											},
										});
									}}
								/>
							)}
						</Box>
					</>
				)}
			</Flex>

			<Separator />
		</>
	);
}
